import { useState } from 'react';
import { Loader2Icon } from 'lucide-react';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/toast/toast';
import notifyHB from '@/config/notifyHB';
import { UserDocument } from '@/entities/user-document';
import { revokePublicRetrieval } from '@/use-cases/revoke-public-retrieval';

type RevokePublicRetrievalDialogProps = {
	document: UserDocument | null;
	onClose: () => void;
	// Called once the document is withdrawn, so the row can be updated. Also
	// called on a 409: the backend already considers it withdrawn.
	onRevoked: (document: UserDocument, publicRevokedAt: string | null) => void;
};

export const RevokePublicRetrievalDialog = ({
	document,
	onClose,
	onRevoked,
}: RevokePublicRetrievalDialogProps) => {
	const [isRevoking, setIsRevoking] = useState(false);

	const handleConfirm = async () => {
		if (!document) return;

		setIsRevoking(true);
		const result = await revokePublicRetrieval(document.Serial);
		setIsRevoking(false);

		switch (result.status) {
			case 'ok':
				toast({
					mode: 'default',
					description: 'Se retiró la descarga pública del documento',
				});
				onRevoked(document, result.publicRevokedAt);
				onClose();
				return;
			case 'conflict':
				// Already withdrawn, or someone tried to re-enable it. The row
				// is stale, so correct it instead of leaving the action armed.
				toast({
					mode: 'caution',
					description:
						'La descarga pública ya estaba retirada para este documento',
				});
				onRevoked(document, null);
				onClose();
				return;
			case 'forbidden':
				toast({
					mode: 'destructive',
					description:
						'No tienes permiso para retirar la descarga pública de este documento',
				});
				onClose();
				return;
			default:
				notifyHB(
					'my-documents',
					'RevokePublicRetrievalDialog',
					`Failed to revoke public retrieval for ${document.Serial}`,
				);
				toast({
					mode: 'destructive',
					description:
						'No fue posible retirar la descarga pública. Intenta de nuevo más tarde',
				});
		}
	};

	return (
		<AlertDialog
			open={document !== null}
			onOpenChange={(open) => {
				if (!open && !isRevoking) onClose();
			}}
		>
			<AlertDialogContent data-testid="revoke-public-retrieval-dialog">
				<AlertDialogHeader>
					<AlertDialogTitle>
						¿Retirar la descarga pública?
					</AlertDialogTitle>
					<AlertDialogDescription>
						Esta acción no se puede deshacer. Se eliminará la copia
						pública de «{document?.Path}» y nadie podrá volver a
						descargarla con el código QR impreso. La descarga pública
						no se puede volver a habilitar para este documento. El
						registro de trazabilidad seguirá visible.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={isRevoking}>
						Cancelar
					</AlertDialogCancel>
					<AlertDialogAction
						disabled={isRevoking}
						onClick={(event) => {
							// Keep the dialog open until the request resolves.
							event.preventDefault();
							handleConfirm();
						}}
					>
						{isRevoking && <Loader2Icon className="animate-spin" />}
						Retirar descarga pública
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};
